import { useEffect, useState } from 'react';
import { useClient } from 'urql';

import type { CustomRoleTaxonomySelection } from '@revelio/data-access';

import { fetchRoleSelectionListForTaxonomy } from '../api/fetchRoleSelectionLists';
import type { RoleSelectionListKey, SelectionListState } from '../types';
import { validateRoleSelectionLists } from '../utils/validation';

type RoleSelectionListState = SelectionListState<RoleSelectionListKey>;

/**
 * Shared hook for fetching role selection lists
 * @param taxonomySelection - The taxonomy selection to fetch role lists for
 */
export const useRoleSelectionList = (
  taxonomySelection?: CustomRoleTaxonomySelection
): RoleSelectionListState | null => {
  const gqlClient = useClient();
  const [roleSelectionList, setRoleSelectionList] =
    useState<RoleSelectionListState | null>(null);

  // biome-ignore lint/correctness/useExhaustiveDependencies: intentional dependency list
  useEffect(() => {
    if (!taxonomySelection) {
      setRoleSelectionList(null);
      return;
    }

    (async () => {
      try {
        const result = await fetchRoleSelectionListForTaxonomy({
          taxonomySelection,
          gqlClient,
        });

        if (result && validateRoleSelectionLists(result)) {
          setRoleSelectionList(result as RoleSelectionListState);
        }
      } catch (error) {
        console.error('Error fetching role selection lists:', error);
        setRoleSelectionList(null);
      }
    })();
  }, [taxonomySelection]);

  return roleSelectionList;
};
